"use client";
import {
  ColumnDef,
  flexRender,
  SortingState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { ArrowUpRight, Download, Ellipsis } from "lucide-react";
import * as React from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";

import { Button } from "./button";
import { CardTitle } from "./card";
import { DataTableViewOptions } from "./data-table-view-options";
import { Label } from "./label";
import { Skeleton } from "./skeleton";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enablePagination?: boolean;
  title?: string;
  viewAll?: {
    visible?: boolean;
    url: string;
  };
  withLoading?: boolean;
  toggle?: {
    title: string;
    visible: boolean;
    value?: boolean;
    onChange: React.Dispatch<React.SetStateAction<boolean>>;
  };
  hideCols?: VisibilityState;
  onlyTable?: boolean;
  hideColDropdown?: boolean;
  showTitle?: boolean;
  emptyMessage?: string;
  defaultPerPage?: "10" | "20" | "30" | "40" | "50" | "100";
  onClick?: any;
  exportOrders?: {
    visible: boolean;
    onClick: React.MouseEventHandler<HTMLElement>;
  };
  extraSection?: React.ReactNode;
  serverPagination?: {
    pageCount: number;
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPaginationChange: (pageIndex: number, pageSize: number) => void;
  };
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enablePagination = false,
  title,
  viewAll,
  withLoading = false,
  toggle,
  hideCols,
  onlyTable,
  emptyMessage,
  hideColDropdown,
  showTitle = true,
  defaultPerPage = "10",
  onClick,
  exportOrders,
  extraSection,
  serverPagination,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  React.useEffect(() => {
    if (hideCols) {
      setColumnVisibility((st) => ({ ...st, ...hideCols }));
    }
  }, [hideCols]);

  const table = useReactTable({
    data,
    columns,
    manualPagination: serverPagination ? true : !enablePagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnVisibility,
      ...(serverPagination && {
        pagination: {
          pageIndex: serverPagination.pageIndex,
          pageSize: serverPagination.pageSize,
        },
      }),
    },
    ...(serverPagination && {
      pageCount: serverPagination.pageCount,
      onPaginationChange: (updater) => {
        if (typeof updater === "function") {
          const currentState = {
            pageIndex: serverPagination.pageIndex,
            pageSize: serverPagination.pageSize,
          };
          const newState = updater(currentState);
          serverPagination.onPaginationChange(
            newState.pageIndex,
            newState.pageSize,
          );
        } else {
          serverPagination.onPaginationChange(
            updater.pageIndex,
            updater.pageSize,
          );
        }
      },
    }),
  });

  React.useEffect(() => {
    if (!serverPagination) {
      table.setPageSize(parseFloat(defaultPerPage));
    }
  }, [defaultPerPage, table, serverPagination]);

  const pageInfo = React.useMemo(() => {
    if (serverPagination) {
      const from = serverPagination.pageIndex * serverPagination.pageSize + 1;
      const to = Math.min(
        (serverPagination.pageIndex + 1) * serverPagination.pageSize,
        serverPagination.totalCount,
      );
      return (
        <p className="text-sm text-muted-foreground">{`${from}-${to} of ${serverPagination.totalCount} ${title}`}</p>
      );
    } else {
      const pageIndex = table.getState().pagination.pageIndex;
      const from = pageIndex * table.getState().pagination.pageSize + 1;
      const to = (pageIndex + 1) * table.getState().pagination.pageSize;
      return (
        <p className="text-sm text-muted-foreground">{`${from}-${Math.min(
          to,
          data.length,
        )} of ${data.length} ${title}`}</p>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, table.getState().pagination, title, serverPagination]);

  const onPress = React.useCallback(
    (row: any) => {
      if (onClick) onClick(row.original);
    },
    [onClick],
  );

  return (
    <div className="min-w-full lg:max-w-[700px] overflow-auto">
      {onlyTable ? (
        <></>
      ) : (
        <div className={cn("flex items-center")}>
          <div className="flex items-center space-x-4 w-full">
            {showTitle && <CardTitle className="">{title}</CardTitle>}
            {toggle?.visible ? (
              <div className="flex items-center space-x-2">
                <Switch
                  defaultChecked={toggle?.value}
                  onCheckedChange={toggle.onChange}
                  id="cancel-toggle"
                />
                <Label className="text-sm font-medium" htmlFor="cancel-toggle">
                  {toggle?.title}
                </Label>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className="w-fit flex items-center gap-2">
            <div className="w-full gap-2 items-center md:flex hidden">
              {viewAll?.visible ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button asChild size="sm" className="ml-auto gap-1">
                        <Link href={viewAll.url}>
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      className="bg-primary text-primary-foreground"
                      side="left"
                    >
                      <p>{m.view_all()} </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <></>
              )}
              {exportOrders?.visible ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={exportOrders.onClick}
                        variant="outline"
                        size="sm"
                        className="ml-auto gap-1"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      className="bg-primary text-primary-foreground"
                      side="left"
                    >
                      <p>{m.export_orders()} </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <></>
              )}
              {extraSection ? extraSection : <></>}
            </div>
            <div className="w-full items-center md:hidden flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {exportOrders?.visible ||
                  extraSection != null ||
                  viewAll?.visible ? (
                    <Button variant="outline" size="sm" className="">
                      <Ellipsis className="h-4 w-4" />
                    </Button>
                  ) : (
                    <></>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 flex flex-col gap-3 p-3">
                  <DropdownMenuItem asChild>
                    {exportOrders?.visible ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              onClick={exportOrders.onClick}
                              variant="outline"
                              size="sm"
                              className="w-full gap-1"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent
                            className="bg-primary text-primary-foreground"
                            side="left"
                          >
                            <p>{m.export_orders()} </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <></>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    {extraSection ? extraSection : <></>}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    {viewAll?.visible ? (
                      <Button asChild size="sm" className="w-full gap-1">
                        <Link href={viewAll.url}>
                          {m.view_all()}
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    ) : (
                      <></>
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {!hideColDropdown && (
              <div className="flex items-center">
                <DataTableViewOptions table={table} />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-4">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth:
                          header.getSize() !== 150
                            ? header.getSize()
                            : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => onPress(row)}
                  className={onClick && "cursor-pointer"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const hasMeta: any =
                      cell.getContext().cell.column.columnDef.meta;
                    return (
                      <TableCell
                        key={cell.id}
                        {...(hasMeta?.getCellContext && {
                          ...hasMeta.getCellContext(cell.getContext()),
                        })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : withLoading ? (
              [...Array(5)].map(() => {
                return (
                  <TableRow key={Math.random() * 100}>
                    <TableCell
                      colSpan={columns.length}
                      className="h-[50px] mb-2"
                    >
                      <Skeleton className="w-full h-full" />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyMessage ?? m.no_results()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {(enablePagination || serverPagination) && (
        <div className="flex items-center justify-between md:px-0 px-4">
          {data.length ||
          (serverPagination && serverPagination.totalCount > 0) ? (
            pageInfo
          ) : (
            <div />
          )}
          <div className="flex items-center justify-end gap-4">
            <Select
              value={
                serverPagination ? String(serverPagination.pageSize) : undefined
              }
              defaultValue={
                serverPagination
                  ? String(serverPagination.pageSize)
                  : defaultPerPage
              }
              onValueChange={(size) => {
                if (serverPagination) {
                  serverPagination.onPaginationChange(0, parseFloat(size));
                } else {
                  table.setPageSize(parseFloat(size));
                }
              }}
            >
              <SelectTrigger className="w-fit">
                <SelectValue placeholder="10" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="40">40</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center justify-end space-x-2 py-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  table.previousPage();
                  document.getElementById("pairs-list-container")?.scrollTo({
                    behavior: "smooth",
                    top: 0,
                  });
                }}
                disabled={!table.getCanPreviousPage()}
              >
                {m.previous()}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  table.nextPage();
                  document.getElementById("pairs-list-container")?.scrollTo({
                    behavior: "smooth",
                    top: 0,
                  });
                }}
                disabled={!table.getCanNextPage()}
              >
                {m.next()}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
